import type { PipeRouteOptions, PipeRoutePortWorld, PipeRouteResult } from './pipeRoute.types'

const EPS = 1e-6

function snap(n: number, gridSize: number): number {
  if (gridSize <= EPS) return n
  return Math.round(n / gridSize) * gridSize
}

function dedupe(points: Array<{ x: number; y: number; z: number }>, minSegmentLength: number) {
  if (points.length <= 1) return points
  const out: Array<{ x: number; y: number; z: number }> = [points[0]!]
  for (let i = 1; i < points.length; i++) {
    const prev = out[out.length - 1]!
    const cur = points[i]!
    const dx = cur.x - prev.x
    const dy = cur.y - prev.y
    const dz = cur.z - prev.z
    if (Math.hypot(dx, dy, dz) >= minSegmentLength) out.push(cur)
  }
  if (out.length === 1 && points.length > 1) out.push(points[points.length - 1]!)
  return out
}

export function buildOrthogonalPipeRoute(
  start: PipeRoutePortWorld,
  end: PipeRoutePortWorld,
  options: PipeRouteOptions = {},
): PipeRouteResult {
  const gridSize = Math.max(0, Number(options.gridSize ?? 1))
  const minSegmentLength = Math.max(EPS, Number(options.minSegmentLength ?? 0.05))
  const forceOrthogonal = options.forceOrthogonal !== false
  const sy = start.position.y
  const ey = end.position.y
  const y = Math.abs(sy - ey) < 1e-3 ? sy : ey
  const s = { x: snap(start.position.x, gridSize), y, z: snap(start.position.z, gridSize) }
  const e = { x: snap(end.position.x, gridSize), y, z: snap(end.position.z, gridSize) }
  if (!forceOrthogonal) return { waypoints: dedupe([s, e], minSegmentLength) }
  if (Math.abs(s.x - e.x) < EPS || Math.abs(s.z - e.z) < EPS) {
    return { waypoints: dedupe([s, e], minSegmentLength) }
  }
  const m1 = { x: e.x, y, z: s.z }
  const m2 = { x: s.x, y, z: e.z }
  const d1 = Math.abs(e.x - s.x)
  const d2 = Math.abs(e.z - s.z)
  const chooseM1 = d1 >= d2
  return { waypoints: dedupe([s, chooseM1 ? m1 : m2, e], minSegmentLength) }
}
